import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface StandingEntry {
  position: number;
  unit_id: string;
  display_name: string;
  user_id?: string | null;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    level?: string;
    photo_url?: string;
  } | null;
  pair_id?: number | null;
  team_id?: number | null;
  played: number;
  won: number;
  lost: number;
  points: number;
  sets_won: number;
  sets_lost: number;
  set_diff: number;
  games_won: number;
  games_lost: number;
  game_diff: number;
  is_qualified: boolean;
}

interface Props {
  groupName: string;
  standings: StandingEntry[];
  qualifiersPerGroup: number;
  byeCount?: number;
  setLabel?: string;
}

function EntryName({ entry }: { entry: StandingEntry }) {
  if (entry.team_id) {
    return (
      <Link to={`/teams/${entry.team_id}`} className="font-medium text-gray-900 hover:text-orange-600">
        {entry.display_name}
      </Link>
    );
  }
  if (entry.user_id) {
    return (
      <Link to={`/users/${entry.user_id}`} className="font-medium text-gray-900 hover:text-primary-600">
        {entry.display_name}
        {entry.user?.level && <span className="ml-2 text-xs text-gray-400">{entry.user.level}</span>}
      </Link>
    );
  }
  return <span className="font-medium text-gray-900">{entry.display_name}</span>;
}

export default function GroupTable({ groupName, standings, qualifiersPerGroup, byeCount = 0, setLabel }: Props) {
  const { t } = useTranslation();
  const resolvedSetLabel = setLabel ?? t('groupTable.played');

  return (
    <div className="card bg-primary-100 border-primary-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('groupTable.group')} {groupName}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-2 pr-3 pl-1 text-gray-500 font-medium w-8 border-r border-gray-200">{t('groupTable.pos')}</th>
              <th className="text-left py-2 px-3 text-gray-500 font-medium border-r border-gray-200">{t('groupTable.name')}</th>
              <th className="text-center py-2 px-2 text-gray-500 font-medium w-10 border-r border-gray-200">{t('groupTable.played')}</th>
              <th className="text-center py-2 px-2 text-gray-500 font-medium w-10 border-r border-gray-200">{t('groupTable.won')}</th>
              <th className="text-center py-2 px-2 text-gray-500 font-medium w-10 border-r border-gray-200">{t('groupTable.lost')}</th>
              <th className="text-center py-2 px-2 text-gray-900 font-semibold w-14 border-r border-gray-200">{t('groupTable.points')}</th>
              <th className="text-center py-2 px-2 text-gray-500 font-medium w-16 border-r border-gray-200">{resolvedSetLabel}</th>
              <th className="text-center py-2 px-2 text-gray-500 font-medium w-16">{t('groupTable.diff')}</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((entry) => (
              <tr
                key={entry.unit_id}
                className={`border-b border-gray-200 last:border-0 ${
                  entry.is_qualified ? 'bg-green-50' : 'odd:bg-white even:bg-gray-50'
                }`}
              >
                <td className="py-2 pr-3 pl-1 border-r border-gray-200">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                    entry.position <= qualifiersPerGroup
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {entry.position}
                  </span>
                </td>
                <td className="py-2 px-3 border-r border-gray-200">
                  <EntryName entry={entry} />
                </td>
                <td className="text-center py-2 px-2 text-gray-700 border-r border-gray-200">{entry.played}</td>
                <td className="text-center py-2 px-2 text-green-600 font-medium border-r border-gray-200">{entry.won}</td>
                <td className="text-center py-2 px-2 text-red-500 border-r border-gray-200">{entry.lost}</td>
                <td className="text-center py-2 px-2 font-bold text-gray-900 border-r border-gray-200">{entry.points}</td>
                <td className="text-center py-2 px-2 text-gray-500 border-r border-gray-200">
                  {entry.sets_won}:{entry.sets_lost}
                </td>
                <td className={`text-center py-2 px-2 font-medium ${
                  entry.set_diff > 0 ? 'text-green-600' : entry.set_diff < 0 ? 'text-red-500' : 'text-gray-500'
                }`}>
                  {entry.set_diff > 0 ? '+' : ''}{entry.set_diff}
                </td>
              </tr>
            ))}
            {byeCount > 0 && Array.from({ length: byeCount }).map((_, i) => (
              <tr key={`bye-${i}`} className="border-b border-gray-200 last:border-0 opacity-50">
                <td className="py-2 pr-3 pl-1 border-r border-gray-200">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-gray-100 text-gray-400">
                    –
                  </span>
                </td>
                <td className="py-2 px-3 border-r border-gray-200">
                  <span className="text-gray-400 italic text-sm">{t('groupTable.bye')}</span>
                </td>
                <td colSpan={6} className="py-2 px-2 text-center text-xs text-gray-300">–</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {qualifiersPerGroup > 0 && standings.length > 0 && (
        <p className="mt-3 text-xs text-gray-400 flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-green-100" />
          {t('groupTable.qualifyText', { count: qualifiersPerGroup })}
        </p>
      )}
    </div>
  );
}
